<script lang="ts">
  import { onMount, onDestroy, untrack } from 'svelte';
  import {
    type ChatDocument,
    documentsByChat,
    uploadChatDocumentBatch,
    listChatDocuments,
    deleteChatDocument,
    hasPendingDocuments,
    isSupportedDocumentType,
    MAX_CONTEXT_BATCH_DOCUMENTS,
    MAX_CHAT_DOCUMENT_SIZE,
  } from '../../chats.svelte';
  import { showGlobalError, showGlobalSuccess } from '../../errorBanner.svelte';
  import ConfirmModal from '../common/ConfirmModal.svelte';
  import { currentUser } from '../../auth.svelte';
  import type { Palette } from '../../theme.svelte';

  let { chatId, palette } = $props<{
    chatId: string;
    palette: Palette;
  }>();

  // ── State ──────────────────────────────────────────────────
  let expanded = $state(false);
  let uploading = $state(false);
  let deletingId = $state<number | null>(null);
  let pendingDelete = $state<ChatDocument | null>(null);
  let showHelp = $state(false);
  let dragActive = $state(false);
  let dragDepth = 0;
  let popoverSnoozed = $state(false);
  let fileInput: HTMLInputElement | undefined = $state();

  // Documents for this chat (reactive)
  let docs = $derived(documentsByChat[chatId] ?? []);
  let hasPending = $derived(hasPendingDocuments(chatId));
  let completedCount = $derived(docs.filter(doc => doc.status === 'completed').length);
  let activeCount = $derived(docs.filter(doc => ['uploading', 'pending', 'processing', 'retrying'].includes(doc.status)).length);

  let summary = $derived.by(() => {
    if (docs.length === 0) return 'Carga masiva · Queda guardado · La IA lo consulta';
    if (hasPending) return `${completedCount} de ${docs.length} listos · procesando…`;
    return docs.length === 1
      ? '1 documento · disponible para la IA'
      : `${docs.length} documentos · disponibles para la IA`;
  });

  // ── Onboarding: aviso flotante descartable ──────────────────
  // La clave está versionada: al cambiar la experiencia de onboarding se sube
  // la versión para que el aviso pueda mostrarse una vez más a todos.
  // v1 → tarjeta incrustada (reemplazada); v2 → popover flotante.
  const ONBOARDING_PREFIX = 'jm_ctx_onboarding_v2';

  function onboardingKey(): string {
    return `${ONBOARDING_PREFIX}:${currentUser.username ?? 'anon'}`;
  }

  function readOnboardingDismissed(): boolean {
    try {
      return localStorage.getItem(onboardingKey()) === '1';
    } catch {
      return false;
    }
  }

  let welcomeDismissed = $state(readOnboardingDismissed());
  let dismissedThisSession = $state(false);

  // La autenticación se resuelve después del primer render: si el usuario
  // pasa de "anon" a su nombre real, re-leemos la marca de onboarding.
  $effect(() => {
    const key = `${ONBOARDING_PREFIX}:${currentUser.username ?? 'anon'}`;
    if (dismissedThisSession) return;
    untrack(() => {
      try {
        welcomeDismissed = localStorage.getItem(key) === '1';
      } catch {
        // localStorage no disponible: mantenemos el estado actual
      }
    });
  });

  function dismissWelcome() {
    dismissedThisSession = true;
    welcomeDismissed = true;
    try {
      localStorage.setItem(onboardingKey(), '1');
    } catch {
      // sin persistencia disponible
    }
  }

  // Aviso flotante no invasivo: solo con la base vacía, el panel replegado y
  // sin arrastres en curso. "Ahora no" (X) lo oculta hasta la próxima recarga.
  let showPopover = $derived(
    !welcomeDismissed &&
    !dismissedThisSession &&
    !popoverSnoozed &&
    !expanded &&
    !dragActive &&
    docs.length === 0
  );

  function snoozePopover() {
    popoverSnoozed = true;
  }

  // Si el usuario ya tiene documentos, el aviso no debe volver a aparecer
  // (p. ej. si más adelante elimina todos los de la base).
  $effect(() => {
    if (docs.length > 0 && !welcomeDismissed) {
      dismissWelcome();
    }
  });

  // ── Polling for status updates ─────────────────────────────
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(async () => {
      if (!hasPendingDocuments(chatId)) {
        stopPolling();
        return;
      }
      try {
        await listChatDocuments(chatId);
      } catch {
        // Silently retry next tick
      }
    }, 3000);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  $effect(() => {
    // React to pending docs: start/stop polling
    if (hasPending) {
      startPolling();
    } else {
      stopPolling();
    }
  });

  // ── Per-chat load ──────────────────────────────────────────
  // El panel vive dentro de ChatView y se reutiliza al cambiar de chat,
  // así que recargamos y reposicionamos el estado en cada chatId.
  let loadedChatId = '';

  $effect(() => {
    const id = chatId;
    if (id === loadedChatId) return;
    loadedChatId = id;
    untrack(() => {
      // La base documental siempre arranca replegada: el aviso flotante (o el
      // usuario) guía hacia ella sin empujar la conversación.
      expanded = false;
      showHelp = false;
      dragActive = false;
      dragDepth = 0;
      // Reinicia el polling por chat: si el nuevo chat ya tiene documentos
      // pendientes (p. ej. una carga iniciada antes de cambiar de chat),
      // arranca de inmediato; el efecto de hasPending hace el resto.
      stopPolling();
      if (hasPendingDocuments(id)) startPolling();
      void listChatDocuments(id).catch(() => {});
    });
  });

  onMount(() => {
    window.addEventListener('dragend', resetDragState);
  });

  onDestroy(() => {
    stopPolling();
    window.removeEventListener('dragend', resetDragState);
  });

  // ── Validation / Upload ────────────────────────────────────
  function validateContextFiles(files: File[]): string | null {
    if (files.length === 0) return null;
    if (files.length > MAX_CONTEXT_BATCH_DOCUMENTS) {
      return `Puedes cargar máximo ${MAX_CONTEXT_BATCH_DOCUMENTS} archivos por carga.`;
    }
    for (const file of files) {
      if (!isSupportedDocumentType(file.type)) {
        return `Formato de "${file.name}" no soportado. Usa PDF o imagen (JPG, PNG, WEBP, TIFF, BMP, GIF).`;
      }
      if (file.size <= 0 || file.size > MAX_CHAT_DOCUMENT_SIZE) {
        return `"${file.name}" excede el límite de 49 MB.`;
      }
    }
    return null;
  }

  /**
   * Agrega archivos a la base documental del chat.
   * Expuesto como método de instancia para que ChatView pueda enrutar aquí
   * los soltados de más de 3 archivos que llegan por el DropZone global.
   */
  export async function addContextFiles(files: File[]): Promise<void> {
    if (uploading || files.length === 0) return;

    const validationError = validateContextFiles(files);
    if (validationError) {
      showGlobalError(validationError);
      return;
    }

    uploading = true;
    try {
      const result = await uploadChatDocumentBatch(chatId, files, 'context');
      startPolling();
      if (result.failedFiles.length > 0) {
        showGlobalError(`No se pudieron subir: ${result.failedFiles.join(', ')}`);
      }
      const added = result.documents.length;
      if (added > 0) {
        showGlobalSuccess(
          added === 1
            ? '1 documento agregado a la base documental. La IA lo consultará al terminar de procesarlo.'
            : `${added} documentos agregados a la base documental. La IA los consultará al terminar de procesarlos.`
        );
      }
    } catch (err: any) {
      showGlobalError(err.message || 'Error al subir el documento.');
    } finally {
      uploading = false;
    }
  }

  function openFilePicker() {
    fileInput?.click();
  }

  async function handleFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) return;
    await addContextFiles(files);
  }

  // ── Drag & drop sobre el panel ─────────────────────────────
  function isFileDrag(e: DragEvent): boolean {
    const types = e.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  }

  function resetDragState() {
    dragDepth = 0;
    dragActive = false;
  }

  function handlePanelDragEnter(e: DragEvent) {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth += 1;
    dragActive = true;
  }

  function handlePanelDragOver(e: DragEvent) {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    dragActive = true;
  }

  function handlePanelDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth = Math.max(0, dragDepth - 1);
    const el = e.currentTarget as HTMLElement;
    const related = e.relatedTarget as Node | null;
    if (dragDepth === 0 || !related || !el.contains(related)) {
      resetDragState();
    }
  }

  function handlePanelDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    resetDragState();
    if (uploading) return;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    void addContextFiles(files);
  }

  // ── Delete handlers ────────────────────────────────────────
  function requestDelete(doc: ChatDocument) {
    if (deletingId !== null || doc.status === 'processing') return;
    pendingDelete = doc;
  }

  function cancelDelete() {
    pendingDelete = null;
  }

  async function confirmDelete() {
    const doc = pendingDelete;
    pendingDelete = null;
    if (!doc) return;

    deletingId = doc.id;
    try {
      await deleteChatDocument(chatId, doc.id);
      showGlobalSuccess(`«${doc.filename}» se eliminó de la base documental.`);
    } catch (err: any) {
      showGlobalError(err.message || 'Error al eliminar el documento.');
    } finally {
      deletingId = null;
    }
  }

  // ── Helpers ────────────────────────────────────────────────
  function statusIcon(status: string): string {
    switch (status) {
      case 'pending':    return 'bi-hourglass-split';
      case 'uploading':  return 'bi-cloud-arrow-up';
      case 'processing': return 'bi-arrow-repeat';
      case 'retrying':   return 'bi-arrow-clockwise';
      case 'completed':  return 'bi-check-circle-fill';
      case 'failed':     return 'bi-exclamation-triangle-fill';
      default:           return 'bi-question-circle';
    }
  }

  function statusColor(status: string): string {
    switch (status) {
      case 'completed':  return '#22c55e';
      case 'processing': return '#3b82f6';
      case 'retrying':   return '#f59e0b';
      case 'pending':    return '#f59e0b';
      case 'uploading':  return '#8b5cf6';
      case 'failed':     return '#ef4444';
      default:           return '#6b7280';
    }
  }

  function statusLabel(status: string): string {
    switch (status) {
      case 'pending':    return 'En cola';
      case 'uploading':  return 'Subiendo…';
      case 'processing': return 'Procesando…';
      case 'retrying':   return 'Reintentando…';
      case 'completed':  return 'Disponible para la IA';
      case 'failed':     return 'Error';
      default:           return status;
    }
  }

  function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CO', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  function fileIcon(mimeType: string): string {
    if (mimeType === 'application/pdf') return 'bi-file-earmark-pdf';
    if (mimeType.startsWith('image/')) return 'bi-file-image';
    return 'bi-file-earmark';
  }

  function togglePanel() {
    expanded = !expanded;
    if (expanded) {
      // Refresh list when opening
      listChatDocuments(chatId).catch(() => {});
      // Si el usuario ya abrió el panel, el aviso flotante cumplió su misión:
      // no debe volver a aparecer (la info queda en "¿Cómo funciona?").
      if (!welcomeDismissed) dismissWelcome();
    }
  }
</script>

<div
  class="context-wrap"
  role="region"
  aria-label="Base documental del chat: suelta aquí tus documentos"
  ondragenter={handlePanelDragEnter}
  ondragover={handlePanelDragOver}
  ondragleave={handlePanelDragLeave}
  ondrop={handlePanelDrop}
>
  <section
    class="context-card"
    class:drag-active={dragActive}
    style="background: {palette.bgChatInput}; color: {palette.textPrimary}; border-color: {palette.borderSoft}; --accent: {palette.accent};"
    aria-label="Base documental del chat"
  >
    <div class="context-header">
      <button
        class="context-toggle"
        type="button"
        onclick={togglePanel}
        aria-expanded={expanded}
      >
        <span class="context-icon" style="color: {palette.accent};">
          <i class="bi bi-folder2-open"></i>
        </span>
        <span class="context-heading">
          <span class="context-title">Base documental del chat</span>
          <span class="context-summary" style="color: {palette.textSecondary};">
            {summary}
          </span>
        </span>
        {#if hasPending}
          <span class="doc-processing-dot" title="Procesando {activeCount} {activeCount === 1 ? 'documento' : 'documentos'}"></span>
        {/if}
        <i class="bi {expanded ? 'bi-chevron-up' : 'bi-chevron-down'} context-chevron"></i>
      </button>

      <button
        class="context-upload"
        type="button"
        onclick={openFilePicker}
        disabled={uploading}
        title="Agregar documentos a la base documental"
        style="background: {palette.accent};"
      >
        <i class="bi {uploading ? 'bi-hourglass-split' : 'bi-plus-lg'}"></i>
        <span>{uploading ? 'Subiendo…' : 'Agregar documentos'}</span>
      </button>
    </div>

    {#if dragActive}
      <div class="context-drag-overlay" aria-hidden="true">
        <i class="bi bi-cloud-arrow-up"></i>
        <span>Suelta para agregar a la base documental</span>
      </div>
    {/if}

    <input
      type="file"
      bind:this={fileInput}
      onchange={handleFileSelected}
      accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff,.bmp,.gif"
      multiple
      style="display: none;"
    />

    {#if expanded}
      <div class="context-body">
        {#if docs.length === 0}
          <button
            class="drop-zone"
            type="button"
            onclick={openFilePicker}
            disabled={uploading}
            aria-label="Agregar documentos a la base documental"
            title="Agregar documentos a la base documental"
          >
            <i class="bi {uploading ? 'bi-hourglass-split' : 'bi-cloud-arrow-up'} drop-zone-icon"></i>
            <span class="drop-zone-title">
              {uploading ? 'Subiendo documentos…' : 'Arrastra tus documentos aquí'}
            </span>
            <span class="drop-zone-cta" style="color: {palette.textSecondary};">
              o selecciónalos desde tu equipo
            </span>
            <span class="drop-zone-limits" style="color: {palette.textSecondary};">
              PDF e imágenes · hasta {MAX_CONTEXT_BATCH_DOCUMENTS} por carga · máx. 49 MB por archivo
            </span>
          </button>
        {:else}
          <div class="doc-list">
            {#each docs as doc (doc.id)}
              <div class="doc-row">
                <!-- Status icon -->
                <span
                  class="doc-status-icon"
                  style="color: {statusColor(doc.status)};"
                  title="{statusLabel(doc.status)}{doc.error_message ? ': ' + doc.error_message : ''}"
                >
                  <i class="bi {statusIcon(doc.status)} {(doc.status === 'processing' || doc.status === 'retrying') ? 'spin' : ''}"></i>
                </span>

                <!-- File info -->
                <div class="doc-info">
                  <div class="doc-filename" style="color: {palette.textPrimary};">
                    <i class="bi {fileIcon(doc.mime_type)} doc-file-icon"></i>
                    <span>{doc.filename}</span>
                  </div>
                  <div class="doc-meta" style="color: {palette.textSecondary};">
                    <span class="doc-status-label" style="color: {statusColor(doc.status)};">
                      {statusLabel(doc.status)}
                    </span>
                    <span>·</span>
                    <span>{formatDate(doc.created_at)}</span>
                    {#if doc.status === 'failed' && doc.error_message}
                      <span class="doc-error" title={doc.error_message}>
                        · {doc.error_message}
                      </span>
                    {/if}
                  </div>
                </div>

                <!-- Delete button -->
                <button
                  class="doc-delete-btn"
                  onclick={() => requestDelete(doc)}
                  disabled={deletingId === doc.id || doc.status === 'processing'}
                  title={doc.status === 'processing' ? 'Espera a que termine el procesamiento' : 'Eliminar de la base documental'}
                  style="color: {palette.textSecondary};"
                >
                  <i class="bi {deletingId === doc.id ? 'bi-hourglass-split' : 'bi-trash'}"></i>
                </button>
              </div>
            {/each}
          </div>
        {/if}

        <div class="context-foot" style="color: {palette.textSecondary};">
          <span class="context-capacity">
            <i class="bi bi-info-circle"></i>
            Hasta {MAX_CONTEXT_BATCH_DOCUMENTS} documentos por carga
          </span>
          <button
            class="context-help-toggle"
            type="button"
            onclick={() => (showHelp = !showHelp)}
            aria-expanded={showHelp}
            style="color: {palette.accent};"
          >
            ¿Cómo funciona?
            <i class="bi {showHelp ? 'bi-chevron-up' : 'bi-chevron-down'}"></i>
          </button>
        </div>

        {#if showHelp}
          <p class="context-help" style="color: {palette.textSecondary};">
            Son la memoria documental del chat: la IA busca en ellos antes de responder. Si solo
            quieres adjuntar un archivo a un mensaje, usa el clip: allí no quedan guardados.
          </p>
        {/if}
      </div>
    {/if}
  </section>

  {#if showPopover}
    <div
      class="context-popover"
      style="background: {palette.bgChatInput}; color: {palette.textPrimary}; border-color: {palette.borderSoft}; --accent: {palette.accent};"
    >
      <div class="popover-arrow" style="background: {palette.bgChatInput}; border-color: {palette.borderSoft};"></div>
      <div class="popover-head">
        <span class="popover-icon" style="color: {palette.accent};"><i class="bi bi-stars"></i></span>
        <strong>Tu base documental, siempre a mano</strong>
        <button
          class="popover-snooze"
          type="button"
          onclick={snoozePopover}
          title="Ahora no"
          aria-label="Ocultar por ahora"
          style="color: {palette.textSecondary};"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <ul class="popover-points" style="color: {palette.textSecondary};">
        <li>
          <i class="bi bi-stack" style="color: {palette.accent};"></i>
          <span><strong style="color: {palette.textPrimary};">Carga masiva:</strong> hasta {MAX_CONTEXT_BATCH_DOCUMENTS} PDFs o imágenes por lote.</span>
        </li>
        <li>
          <i class="bi bi-pin-angle" style="color: {palette.accent};"></i>
          <span><strong style="color: {palette.textPrimary};">Siempre disponible:</strong> queda guardado; no se pierde al recargar ni al avanzar la conversación.</span>
        </li>
        <li>
          <i class="bi bi-search" style="color: {palette.accent};"></i>
          <span><strong style="color: {palette.textPrimary};">La IA los consulta:</strong> busca en tus documentos antes de responder.</span>
        </li>
      </ul>
      <div class="popover-actions">
        <button
          class="popover-cta"
          type="button"
          onclick={openFilePicker}
          style="background: {palette.accent};"
        >
          Agregar documentos
        </button>
        <button
          class="popover-dismiss"
          type="button"
          onclick={dismissWelcome}
          style="color: {palette.textSecondary};"
        >
          No volver a mostrar
        </button>
      </div>
    </div>
  {/if}
</div>

<ConfirmModal
  open={pendingDelete !== null}
  title="Eliminar de la base documental"
  message={pendingDelete
    ? `«${pendingDelete.filename}» dejará de estar disponible para la IA y se borrarán sus fragmentos indexados. Esta acción no se puede deshacer.`
    : ''}
  confirmLabel="Eliminar"
  cancelLabel="Cancelar"
  {palette}
  isDanger={true}
  onConfirm={confirmDelete}
  onCancel={cancelDelete}
/>

<style>
  .context-wrap {
    position: relative;
    width: 100%;
    max-width: 720px;
    margin: 8px auto 0;
    padding: 0 32px;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  .context-card {
    position: relative;
    border: 1px solid rgba(128, 128, 128, 0.16);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.035);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .context-card.drag-active {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
    /* Por encima del overlay global de arrastre del chat (z-index 50),
       para que se lea como la zona que recibirá el drop. */
    z-index: 60;
  }

  .context-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px 7px 10px;
  }

  .context-toggle {
    min-width: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 9px;
    border: 0;
    background: transparent;
    color: inherit;
    padding: 2px;
    cursor: pointer;
    text-align: left;
    user-select: none;
  }

  .context-icon {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    border-radius: 8px;
    background: color-mix(in srgb, currentColor 10%, transparent);
    font-size: 14px;
  }

  .context-heading {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .context-title {
    font-size: 13px;
    font-weight: 600;
  }

  .context-summary {
    max-width: 380px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 400;
  }

  .context-chevron {
    color: rgba(128, 128, 128, 0.75);
    font-size: 11px;
    flex: 0 0 auto;
  }

  .context-upload {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    border: 0;
    border-radius: 8px;
    color: #fff;
    padding: 6px 10px;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.12s;
  }

  .context-upload:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .context-upload:active:not(:disabled) {
    transform: scale(0.97);
  }

  .context-upload:disabled {
    opacity: 0.55;
    cursor: wait;
  }

  .doc-processing-dot {
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #3b82f6;
    animation: doc-pulse 1.5s ease-in-out infinite;
  }

  @keyframes doc-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.3); }
  }

  /* ── Overlay de arrastre ─────────────────────────────── */
  .context-drag-overlay {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    backdrop-filter: blur(2px);
    color: var(--accent);
    font-size: 12.5px;
    font-weight: 600;
    text-align: center;
    padding: 8px;
  }

  .context-drag-overlay i {
    font-size: 20px;
  }

  /* ── Cuerpo expandido ────────────────────────────────── */
  .context-body {
    border-top: 1px solid rgba(128, 128, 128, 0.13);
    padding: 10px;
  }

  /* ── Aviso flotante de bienvenida ────────────────────── */
  .context-popover {
    position: absolute;
    top: calc(100% + 7px);
    left: 32px;
    right: 32px;
    z-index: 70;
    padding: 11px 12px 12px;
    border: 1px solid;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
    animation: popover-in 0.18s ease-out;
  }

  .popover-arrow {
    position: absolute;
    top: -6px;
    left: 22px;
    width: 10px;
    height: 10px;
    transform: rotate(45deg);
    border-left: 1px solid;
    border-top: 1px solid;
  }

  .popover-head {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .popover-icon {
    font-size: 14px;
    flex: 0 0 auto;
  }

  .popover-head strong {
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
  }

  .popover-snooze {
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-size: 10px;
    cursor: pointer;
  }

  .popover-snooze:hover {
    background: rgba(128, 128, 128, 0.12);
  }

  .popover-points {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .popover-points li {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    font-size: 11px;
    line-height: 1.4;
  }

  .popover-points i {
    margin-top: 1px;
    flex: 0 0 auto;
    font-size: 11px;
  }

  .popover-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
  }

  .popover-cta {
    border: 0;
    border-radius: 8px;
    color: #fff;
    padding: 6px 12px;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s ease, transform 0.12s ease;
  }

  .popover-cta:hover {
    opacity: 0.9;
  }

  .popover-cta:active {
    transform: scale(0.97);
  }

  .popover-dismiss {
    border: 0;
    background: transparent;
    padding: 4px 2px;
    font-size: 10.5px;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }

  @keyframes popover-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Zona de arrastre (base vacía) ───────────────────── */
  .drop-zone {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 16px 12px;
    border: 1.5px dashed color-mix(in srgb, var(--accent) 45%, transparent);
    border-radius: 10px;
    background: color-mix(in srgb, var(--accent) 5%, transparent);
    color: inherit;
    font-family: inherit;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
  }

  .drop-zone:hover:not(:disabled) {
    background: color-mix(in srgb, var(--accent) 10%, transparent);
    border-color: color-mix(in srgb, var(--accent) 65%, transparent);
  }

  .drop-zone:active:not(:disabled) {
    transform: scale(0.995);
  }

  .drop-zone:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  .drop-zone-icon {
    font-size: 22px;
    color: var(--accent);
    margin-bottom: 2px;
  }

  .drop-zone-title {
    font-size: 13px;
    font-weight: 600;
  }

  .drop-zone-cta {
    font-size: 11.5px;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .drop-zone-limits {
    margin-top: 4px;
    font-size: 10.5px;
    opacity: 0.85;
  }

  /* ── Lista de documentos ─────────────────────────────── */
  .doc-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .doc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 8px;
    border-radius: 8px;
    transition: background-color 0.1s;
  }
  .doc-row:hover {
    background: rgba(128, 128, 128, 0.08);
  }

  .doc-status-icon {
    font-size: 18px;
    flex-shrink: 0;
    width: 24px;
    text-align: center;
  }

  .spin {
    animation: doc-spin 1s linear infinite;
  }

  @keyframes doc-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .doc-info {
    flex: 1;
    min-width: 0;
  }

  .doc-filename {
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .doc-file-icon {
    font-size: 14px;
    flex-shrink: 0;
  }

  .doc-meta {
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
  }

  .doc-status-label {
    font-weight: 600;
  }

  .doc-error {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .doc-delete-btn {
    border: none;
    background: transparent;
    font-size: 16px;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;
    flex-shrink: 0;
    transition: background-color 0.1s, color 0.1s;
  }
  .doc-delete-btn:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444 !important;
  }
  .doc-delete-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ── Pie del panel ───────────────────────────────────── */
  .context-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 6px;
    padding: 6px 4px 0;
    border-top: 1px solid rgba(128, 128, 128, 0.1);
    font-size: 10.5px;
  }

  .context-capacity {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .context-help-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    padding: 2px 4px;
    border-radius: 6px;
    font-size: 10.5px;
    font-weight: 600;
    cursor: pointer;
  }

  .context-help-toggle:hover {
    background: rgba(128, 128, 128, 0.1);
  }

  .context-help {
    margin: 6px 4px 0;
    font-size: 11px;
    line-height: 1.45;
  }

  /* ── Accesibilidad de movimiento ─────────────────────── */
  @media (prefers-reduced-motion: reduce) {
    .doc-processing-dot,
    .spin {
      animation: none;
    }
    .context-popover {
      animation: none;
    }
    .context-upload,
    .drop-zone,
    .popover-cta {
      transition: none;
    }
  }

  /* ── Mobile ────────────────────────────────────────────── */
  @media (max-width: 768px) {
    .context-wrap {
      padding: 0 12px;
    }

    .context-summary {
      max-width: 175px;
    }

    .context-upload span {
      display: none;
    }

    .context-upload {
      width: 32px;
      height: 32px;
      justify-content: center;
      padding: 0;
    }

    .context-popover {
      left: 12px;
      right: 12px;
    }

    .drop-zone {
      padding: 13px 10px;
    }

    .doc-meta {
      flex-wrap: wrap;
    }

    .doc-error {
      max-width: 120px;
    }
  }
</style>
